/**
 * Dónde cae un hecho del diario en la lista de mensajes
 * (spec 024 · FR-006, FR-012).
 *
 * El emparejado es por TIPO DE ACTO, no por la hora. El reloj
 * solo ordena el camino y aparta un resto ya recorrido.
 *
 *   persona  — lo dijo ella (nombre, día, cambio…) → su mensaje
 *   dicho    — el bot lo pronunció (horas, cita, anulación…) → ese
 *   eco      — interno (esperando, apartó hueco, fase, idioma) → el
 *              último dicho de ese tramo; no se come otro bocadillo
 *
 * El mensaje del bot a menudo se sella unos segundos *después*
 * del hecho (sale a Chatwoot). Sin ese margen, «Le ofreció horas»
 * cae en la pregunta del día.
 */

const PERSONA = new Set([
    'consentimiento_otorgado',
    'consentimiento_rechazado',
    'nombre_guardado',
    'correo_guardado',
    'correo_declinado',
    'servicio_fijado',
    'profesional_fijado',
    'fecha_fijada',
    'cambio_iniciado',
    'espera_apuntada',
])

const DICHO = new Set([
    'huecos_ofrecidos',
    'cita_creada',
    'cita_anulada',
    'espera_ofrecida',
    'espera_oferta_enviada',
    'traspasada',
    'cerrada',
    'umbral.despedida',
    'gestion.cerradoEseDia',
    'gestion.sinHuecos',
])

/** Margen para el sello de Chatwoot respecto al diario. */
const GRACIA_BOT_MS = 15_000

export type DireccionMensaje = 'in' | 'out' | 'other'
export type PapelMensaje = 'mensaje' | 'actividad' | 'nota'
export type ClaseHecho = 'persona' | 'dicho' | 'eco'

export type MensajeEmparejable = {
    at: string | null
    direction?: DireccionMensaje
    papel?: PapelMensaje
}

export function claseDelHecho(hecho: string | undefined): ClaseHecho | null {
    if (!hecho) return null
    if (PERSONA.has(hecho)) return 'persona'
    if (DICHO.has(hecho)) return 'dicho'
    return 'eco'
}

export function ladoDelHecho(hecho: string | undefined): 'in' | 'out' | null {
    const c = claseDelHecho(hecho)
    if (c === 'persona') return 'in'
    if (c === 'dicho' || c === 'eco') return 'out'
    return null
}

function esBocadillo(m: MensajeEmparejable | undefined): boolean {
    if (!m) return false
    return (m.papel ?? 'mensaje') === 'mensaje'
}

export function emparejarPasosMensajes(
    mensajes: readonly MensajeEmparejable[],
    pasos: readonly { at: string | null; hecho?: string }[],
): (number | null)[] {
    const usados = new Set<number>()
    const indices: (number | null)[] = new Array(pasos.length).fill(null)
    let cursor = -1
    let ultimoDicho: number | null = null

    let i = 0
    while (i < pasos.length) {
        const paso = pasos[i]
        if (!paso) {
            i += 1
            continue
        }
        const lado = ladoDelHecho(paso.hecho)
        let fin = i + 1
        while (fin < pasos.length && pasos[fin]?.at === paso.at && ladoDelHecho(pasos[fin]?.hecho) === lado) {
            fin += 1
        }

        const grupo = []
        for (let k = i; k < fin; k++) grupo.push(k)
        const dichos = grupo.filter((k) => claseDelHecho(pasos[k]?.hecho) === 'dicho')
        const ecos = grupo.filter((k) => claseDelHecho(pasos[k]?.hecho) === 'eco')
        const cuantos = dichos.length > 0 ? dichos.length : grupo.length
        const topeTurno = topeDelTurno(pasos, fin, paso.at)
        const elegidos = elegirVarios(mensajes, paso, lado, usados, cuantos, cursor, topeTurno)

        if (dichos.length > 0) {
            const porDicho = new Map<number, number | null>()
            for (let k = 0; k < dichos.length; k++) {
                const idx = elegidos[k] ?? null
                const pos = dichos[k]
                if (pos === undefined) continue
                porDicho.set(pos, idx)
                indices[pos] = idx
                if (idx !== null) {
                    usados.add(idx)
                    if (idx > cursor) cursor = idx
                    ultimoDicho = idx
                }
            }
            for (const pos of ecos) {
                let dueño: number | null = null
                for (const d of dichos) {
                    if (d <= pos) dueño = porDicho.get(d) ?? dueño
                }
                indices[pos] = dueño ?? elegidos[0] ?? ultimoDicho
            }
        } else {
            for (let k = 0; k < grupo.length; k++) {
                const pos = grupo[k]
                if (pos === undefined) continue
                const idx = elegidos[k] ?? null
                indices[pos] = idx
                if (idx !== null) {
                    usados.add(idx)
                    if (idx > cursor) cursor = idx
                    if (lado === 'out') ultimoDicho = idx
                }
            }
        }

        i = fin
    }
    return indices
}

function topeDelTurno(
    pasos: readonly { at: string | null }[],
    desde: number,
    at: string | null,
): number | null {
    if (!at) return null
    const t = Date.parse(at)
    if (!Number.isFinite(t)) return null
    let tope = t + 60_000
    for (let k = desde; k < pasos.length; k++) {
        const siguiente = pasos[k]?.at
        if (!siguiente) continue
        const ts = Date.parse(siguiente)
        if (Number.isFinite(ts) && ts > t + 100) {
            tope = Math.min(tope, ts + GRACIA_BOT_MS)
            break
        }
    }
    return tope
}

function elegirVarios(
    mensajes: readonly MensajeEmparejable[],
    paso: { at: string | null; hecho?: string },
    lado: 'in' | 'out' | null,
    usados: ReadonlySet<number>,
    cuantos: number,
    cursor: number,
    topeTurno: number | null,
): (number | null)[] {
    if (paso.at === null || mensajes.length === 0 || cuantos <= 0) {
        return Array.from({ length: cuantos }, () => null)
    }
    const t = Date.parse(paso.at)
    if (!Number.isFinite(t)) return Array.from({ length: cuantos }, () => null)

    const gracia = lado === 'out' ? GRACIA_BOT_MS : 0
    const hasta: number[] = []
    for (let i = 0; i < mensajes.length; i++) {
        if (i <= cursor) continue
        if (usados.has(i)) continue
        const m = mensajes[i]
        if (!esBocadillo(m)) continue
        const a = m?.at
        if (!a) continue
        const mt = Date.parse(a)
        if (!Number.isFinite(mt) || mt > t + gracia) continue
        if (lado && m?.direction !== lado) continue
        hasta.push(i)
    }

    if (hasta.length === 0 && lado === 'out' && topeTurno !== null) {
        for (let i = cursor + 1; i < mensajes.length; i++) {
            if (usados.has(i)) continue
            const m = mensajes[i]
            if (!esBocadillo(m) || m?.direction !== 'out') continue
            const a = m.at
            if (!a) continue
            const mt = Date.parse(a)
            if (!Number.isFinite(mt) || mt > topeTurno) break
            hasta.push(i)
            break
        }
    }

    if (hasta.length === 0) {
        return Array.from({ length: cuantos }, () => null)
    }
    const tomados = hasta.slice(-cuantos)
    const ultimo = tomados[tomados.length - 1] ?? null
    const hueco = cuantos - tomados.length
    return [...tomados, ...Array.from({ length: hueco }, () => ultimo)]
}

/** Un solo paso, sin consumir. Para pruebas del recorte por hora. */
export function indiceMensajeDelPaso(
    mensajes: readonly MensajeEmparejable[],
    pasoAt: string | null,
    hecho?: string,
): number | null {
    const paso = hecho === undefined ? { at: pasoAt } : { at: pasoAt, hecho }
    return emparejarPasosMensajes(mensajes, [paso])[0] ?? null
}
